/**
 * Thread Admin Hook
 * 
 * Manages Thread API operations (operational, non-CRUD):
 * - Thread listing by workspace
 * - Thread creation and retrieval
 * - Participant management (add/remove)
 * - Message operations (get, batch add)
 * - Statistics (unread count, participant count)
 * - WebSocket events for real-time updates
 */

import { useState, useCallback, useEffect } from "react";
import type { UseAdminAppReturn } from "./useAdminApp";
import { type messageStructureTypes } from "@pineseed/spec";

type Thread = messageStructureTypes["Thread"];
type ChatMessage = messageStructureTypes["ChatMessage"];
type ParticipantUpdateEvent = messageStructureTypes["ParticipantUpdateEvent"];

export interface ThreadStats {
  participantCount: number;
  unreadCount: number;
}

export interface ThreadAdminHandler {
  // State
  threads: Thread[];
  currentThread: Thread | null;
  participants: string[];
  messages: ChatMessage[];
  stats: ThreadStats | null;
  loading: boolean;
  error: string | null;

  // Thread operations
  loadThreads: (limit: number, offset: number) => Promise<Thread[]>;
  getThread: (threadId: string) => Promise<Thread | null>;
  createThread: (data: { threadId: string; conversationId: string; workspaceId: string }) => Promise<Thread>;
  clearThread: () => void;

  // Participant operations
  getThreadParticipants: (threadId: string) => Promise<string[]>;
  addParticipant: (threadId: string, participantId: string) => Promise<void>;
  removeParticipant: (threadId: string, participantId: string) => Promise<void>;

  // Message operations
  getThreadMessages: (threadId: string) => Promise<ChatMessage[]>;
  addMessagesToThread: (data: {
    threadId: string;
    conversationId: string;
    workspaceId: string;
    messages: Array<Omit<ChatMessage, 'id' | 'timestamp'>>;
  }) => Promise<void>;

  // Statistics
  getUnreadCount: (threadId: string, participantId: string) => Promise<number>;
  getParticipantCount: (threadId: string) => Promise<number>;
  loadStats: (threadId: string, participantId: string) => Promise<void>;

  // WebSocket subscriptions
  subscribeToThread: (workspaceId: string, conversationId: string, threadId: string) => () => void;
}

export function useThreadAdmin(adminApp: UseAdminAppReturn): ThreadAdminHandler {
  const [threads, setThreads] = useState<Thread[]>([]);
  const [currentThread, setCurrentThread] = useState<Thread | null>(null);
  const [participants, setParticipants] = useState<string[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [stats, setStats] = useState<ThreadStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // GET /threads - List threads in the workspace
  const loadThreads = useCallback(async (limit: number, offset: number): Promise<Thread[]> => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        workspaceId: adminApp.workspaceId,
        limit: String(limit),
        offset: String(offset),
      });

      const response = await fetch(`${adminApp.httpUrl}/api/threads?${params}`, {
        headers: { 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to list threads: ${response.statusText}`);
      }

      const data = await response.json() as { threads: Thread[] };
      setThreads(data.threads);
      console.log(`✅ Loaded ${data.threads.length} threads`);
      return data.threads;
    } catch (err) {
      const errorMsg = `Failed to list threads: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMsg);
      console.error('❌', errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl, adminApp.workspaceId]);

  // GET /thread - Get thread by ID
  const getThread = useCallback(async (threadId: string): Promise<Thread | null> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/thread?threadId=${threadId}`, {
        headers: { 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          console.log(`Thread ${threadId} not found`);
          return null;
        }
        throw new Error(`Failed to get thread: ${response.statusText}`);
      }

      const thread = await response.json() as Thread;
      setCurrentThread(thread);
      console.log('✅ Loaded thread:', threadId);
      return thread;
    } catch (err) {
      const errorMsg = `Failed to get thread: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMsg);
      console.error('❌', errorMsg);
      return null;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // POST /thread - Create new thread
  const createThread = useCallback(async (data: {
    threadId: string;
    conversationId: string;
    workspaceId: string;
  }): Promise<Thread> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/thread`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to create thread: ${response.statusText}`);
      }

      const thread = await response.json() as Thread;
      setCurrentThread(thread);
      console.log('✅ Created thread:', thread.threadId);
      return thread;
    } catch (err) {
      const errorMsg = `Failed to create thread: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMsg);
      console.error('❌', errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // Clear current thread
  const clearThread = useCallback(() => {
    setCurrentThread(null);
    setParticipants([]);
    setMessages([]);
    setStats(null);
    setError(null);
  }, []);

  // GET /thread/participants - Get thread participants
  const getThreadParticipants = useCallback(async (threadId: string): Promise<string[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/thread/participants?threadId=${threadId}`, {
        headers: { 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to get participants: ${response.statusText}`);
      }

      const data = await response.json() as { threadId: string; participants: string[] };
      setParticipants(data.participants);
      console.log(`✅ Loaded ${data.participants.length} participants for thread ${threadId}`);
      return data.participants;
    } catch (err) {
      const errorMsg = `Failed to get participants: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMsg);
      console.error('❌', errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // POST /thread/participant - Add participant to thread
  const addParticipant = useCallback(async (threadId: string, participantId: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/thread/participant`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
        body: JSON.stringify({ threadId, participantId }),
      });

      if (!response.ok) {
        throw new Error(`Failed to add participant: ${response.statusText}`);
      }

      // Refresh participants list
      await getThreadParticipants(threadId);
      console.log(`✅ Added participant ${participantId} to thread ${threadId}`);
    } catch (err) {
      const errorMsg = `Failed to add participant: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMsg);
      console.error('❌', errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl, getThreadParticipants]);

  // DELETE /thread/participant - Remove participant from thread
  const removeParticipant = useCallback(async (threadId: string, participantId: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/thread/participant?threadId=${threadId}&participantId=${participantId}`,
        {
          method: 'DELETE',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to remove participant: ${response.statusText}`);
      }

      // Refresh participants list
      await getThreadParticipants(threadId);
      console.log(`✅ Removed participant ${participantId} from thread ${threadId}`);
    } catch (err) {
      const errorMsg = `Failed to remove participant: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMsg);
      console.error('❌', errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl, getThreadParticipants]);

  // GET /thread/messages - Get thread messages
  const getThreadMessages = useCallback(async (threadId: string): Promise<ChatMessage[]> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/thread/messages?threadId=${threadId}`, {
        headers: { 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to get messages: ${response.statusText}`);
      }

      const data = await response.json() as { threadId: string; messages: ChatMessage[] };
      console.log(data.messages);
      setMessages(data.messages);
      console.log(`✅ Loaded ${data.messages.length} messages for thread ${threadId}`);
      return data.messages;
    } catch (err) {
      const errorMsg = `Failed to get messages: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMsg);
      console.error('❌', errorMsg);
      return [];
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  // POST /thread/messages - Batch add messages to thread
  const addMessagesToThread = useCallback(async (data: {
    threadId: string;
    conversationId: string;
    workspaceId: string;
    messages: Array<Omit<ChatMessage, 'id' | 'timestamp'>>;
  }): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/thread/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error(`Failed to add messages: ${response.statusText}`);
      }

      // Refresh messages list
      await getThreadMessages(data.threadId);
      console.log(`✅ Added ${data.messages.length} messages to thread ${data.threadId}`);
    } catch (err) {
      const errorMsg = `Failed to add messages: ${err instanceof Error ? err.message : 'Unknown error'}`;
      setError(errorMsg);
      console.error('❌', errorMsg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl, getThreadMessages]);

  // GET /thread/unread - Get unread message count
  const getUnreadCount = useCallback(async (threadId: string, participantId: string): Promise<number> => {
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/thread/unread?threadId=${threadId}&participantId=${participantId}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(`Failed to get unread count: ${response.statusText}`);
      }

      const data = await response.json() as { threadId: string; participantId: string; unreadCount: number };
      console.log(`✅ Unread count for ${participantId} in thread ${threadId}: ${data.unreadCount}`);
      return data.unreadCount;
    } catch (err) {
      console.error('❌ Failed to get unread count:', err);
      return 0;
    }
  }, [adminApp.httpUrl]);

  // GET /thread/participant-count - Get participant count
  const getParticipantCount = useCallback(async (threadId: string): Promise<number> => {
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/thread/participant-count?threadId=${threadId}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error(`Failed to get participant count: ${response.statusText}`);
      }

      const data = await response.json() as { threadId: string; participantCount: number };
      console.log(`✅ Participant count for thread ${threadId}: ${data.participantCount}`);
      return data.participantCount;
    } catch (err) {
      console.error('❌ Failed to get participant count:', err);
      return 0;
    }
  }, [adminApp.httpUrl]);

  // Load all stats for a thread
  const loadStats = useCallback(async (threadId: string, participantId: string): Promise<void> => {
    setLoading(true);
    try {
      const [participantCount, unreadCount] = await Promise.all([
        getParticipantCount(threadId),
        getUnreadCount(threadId, participantId),
      ]);

      setStats({ participantCount, unreadCount });
    } finally {
      setLoading(false);
    }
  }, [getParticipantCount, getUnreadCount]);

  // Subscribe to thread events via WebSocket
  const subscribeToThread = useCallback((
    workspaceId: string,
    conversationId: string,
    threadId: string
  ): (() => void) => {
    const channelName = `thread.${workspaceId}.${conversationId}.${threadId}`;
    
    const handleThreadEvent = (event: unknown) => {
      console.log('🔔 [Thread] Received event:', event);
      
      try {
        const wsEvent = event as { type: string; event?: unknown; messages?: ChatMessage[] };
        
        // Handle thread update events (new messages)
        if (wsEvent.type === 'thread' && wsEvent.messages) {
          console.log('📨 [Thread] Thread update with messages:', wsEvent.messages.length);
          setMessages(prev => [...prev, ...wsEvent.messages!]);
        }
        
        // Handle participant update events
        if (wsEvent.type === 'participant' && wsEvent.event) {
          const participantEvent = wsEvent.event as ParticipantUpdateEvent;
          console.log(`👤 [Thread] Participant ${participantEvent.action}:`, participantEvent.participantId);
          
          if (participantEvent.action === 'added') {
            setParticipants(prev => [...new Set([...prev, participantEvent.participantId])]);
          } else if (participantEvent.action === 'removed') {
            setParticipants(prev => prev.filter(p => p !== participantEvent.participantId));
          }
        }
      } catch (err) {
        console.error('❌ [Thread] Error handling event:', err);
      }
    };

    console.log('🔌 [Thread] Subscribing to:', channelName);
    return adminApp.subscribeToChannel(channelName, handleThreadEvent);
  }, [adminApp]);

  // Auto-reload when workspace changes
  useEffect(() => {
    setThreads([]);
    clearThread();
  }, [adminApp.workspaceId, clearThread]);

  return {
    // State
    threads,
    currentThread,
    participants,
    messages,
    stats,
    loading,
    error,

    // Thread operations
    loadThreads,
    getThread,
    createThread,
    clearThread,

    // Participant operations
    getThreadParticipants,
    addParticipant,
    removeParticipant,

    // Message operations
    getThreadMessages,
    addMessagesToThread,

    // Statistics
    getUnreadCount,
    getParticipantCount,
    loadStats,

    // WebSocket subscriptions
    subscribeToThread,
  };
}

